import React, { useRef } from "react";
import classNames from "classnames";

import { PROFILE_SCALE_MAX, ProfileRange } from "../../ShowStylePage/profile";

interface ProfileRangeSliderProps {
  label: string;
  value: ProfileRange;
  onChange: (value: ProfileRange) => void;
  minLabel: string;
  maxLabel: string;
  disabled?: boolean;
}

type Thumb = "min" | "max";

/**
 * Slider with two dots for a range on the 0-5 scale. A range one level wide has both dots
 * on the same level; 0..0 stands for a trait that is not present.
 */
export const ProfileRangeSlider: React.FC<ProfileRangeSliderProps> = ({
  label,
  value,
  onChange,
  minLabel,
  maxLabel,
  disabled,
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const active = useRef<Thumb | null>(null);

  const move = (thumb: Thumb, level: number) => {
    const next = Math.min(PROFILE_SCALE_MAX, Math.max(0, Math.round(level)));
    const range =
      thumb === "min"
        ? { min: Math.min(next, value.max), max: value.max }
        : { min: value.min, max: Math.max(next, value.min) };

    if (range.min !== value.min || range.max !== value.max) onChange(range);
  };

  const levelAt = (clientX: number) => {
    const box = trackRef.current?.getBoundingClientRect();
    if (!box || box.width === 0) return 0;
    return ((clientX - box.left) / box.width) * PROFILE_SCALE_MAX;
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (disabled) return;
    const level = levelAt(event.clientX);

    // The dot nearest to the pointer moves; on a tie the pointer side decides.
    const toMin = Math.abs(level - value.min);
    const toMax = Math.abs(level - value.max);
    active.current =
      toMin === toMax ? (level < value.min ? "min" : "max") : toMin < toMax ? "min" : "max";

    event.currentTarget.setPointerCapture?.(event.pointerId);
    move(active.current, level);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (active.current) move(active.current, levelAt(event.clientX));
  };

  const handlePointerUp = () => {
    active.current = null;
  };

  const handleKeyDown = (thumb: Thumb) => (event: React.KeyboardEvent) => {
    const current = value[thumb];
    const keys: Record<string, number> = {
      ArrowLeft: current - 1,
      ArrowDown: current - 1,
      ArrowRight: current + 1,
      ArrowUp: current + 1,
      Home: 0,
      End: PROFILE_SCALE_MAX,
    };

    if (event.key in keys) {
      event.preventDefault();
      if (!disabled) move(thumb, keys[event.key]);
    }
  };

  const percent = (level: number) => (level / PROFILE_SCALE_MAX) * 100;

  const dot = (thumb: Thumb) => (
    <div
      key={thumb}
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-label={`${label}: ${thumb === "min" ? minLabel : maxLabel}`}
      aria-valuemin={0}
      aria-valuemax={PROFILE_SCALE_MAX}
      aria-valuenow={value[thumb]}
      aria-disabled={disabled}
      onKeyDown={handleKeyDown(thumb)}
      className={classNames(
        "absolute top-1/2 h-4 w-4 -translate-y-1/2 rounded-full border-2 border-solid border-white bg-[#fe932c] shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6c2f00]",
        disabled ? "opacity-50" : "cursor-grab",
      )}
      style={{ left: `calc(${percent(value[thumb])}% - 8px)` }}
    />
  );

  return (
    <div
      ref={trackRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      className="relative h-6 w-full cursor-pointer touch-none select-none"
    >
      <div className="absolute left-0 top-1/2 h-1 w-full -translate-y-1/2 rounded-lg bg-[#dac2b6]" />
      {value.max > value.min && (
        <div
          className="absolute top-1/2 h-1 -translate-y-1/2 rounded-lg bg-[#fe932c]"
          style={{
            left: `${percent(value.min)}%`,
            width: `${percent(value.max) - percent(value.min)}%`,
          }}
        />
      )}
      {dot("min")}
      {dot("max")}
    </div>
  );
};
